import { useScopedI18n } from '@locales/client';
import { useEffect, useMemo } from 'react';

import { useConfirmDataPrompt } from '@/entities/data-confirmation/model/use-confirm-data-prompt';
import { useCashback } from '@/flat-pages/cashback/model/hooks';
import { formatTimestamp } from '@/shared/libs/format-timestamp';
import { ShowOnMobile, ShowOnDesktop } from '@/shared/ui/responsive';
import {
  useIsAuth,
  useUserCurrency,
} from '@/shared/providers/user-provider/use-user';
import { SkeletonLoader } from '@/shared/ui/skeleton/skeleton';
import { BodyTextRegularB, HeadingBoldH7 } from '@/shared/ui/typography2';
import { Button } from '@/shared/ui/form/button/button';
import { ButtonText, ExpiryBox } from '@/flat-pages/cashback/styles';

import {
  DetailFieldLeft,
  DetailFieldRight,
  DetailLabel,
  DetailValue,
  DetailsStyle,
} from './styles';
import { useFormatAmount } from '@/shared/hooks/useFormatAmount';

export const Details = () => {
  const t = useScopedI18n('cashback');
  const isAuth = useIsAuth();
  const currency = useUserCurrency();
  const formatAmount = useFormatAmount({ mode: 'plain' });
  const { data, isLoading, claimRewardsTrigger, mutate, isClaimingLoading } =
    useCashback();
  const { onPopupShow } = useConfirmDataPrompt();

  const isDisabled = !isAuth || isClaimingLoading;
  const showExpiryBox = isAuth && !!data?.tempCashback?.expire;

  useEffect(() => {
    mutate();
  }, [mutate, currency]);

  const getCashbackHandler = async () => {
    if (!isAuth) {
      onPopupShow();
      return;
    }
    try {
      await claimRewardsTrigger();
      await mutate('/cashback/get');
    } catch (error) {
      console.error('Error claiming reward:', error);
    }
  };

  const cashbackValue = useMemo(() => {
    const { percent, tempCashback } = data || {};
    const percentBase = percent
      ? `${percent}${tempCashback?.percent ? '+' : '%'}`
      : '';
    const tempCashbackPercent = tempCashback?.percent
      ? `${tempCashback.percent}%`
      : '';
    return `${percentBase}${tempCashbackPercent}`;
  }, [data?.percent, data?.tempCashback?.percent]);

  const collectedValue = useMemo(() => {
    return formatAmount(data?.sum ?? 0);
  }, [data?.sum, formatAmount]);

  const renderSkeletonOrValue = (value: React.ReactNode) =>
    isClaimingLoading ? (
      <SkeletonLoader
        width="100px"
        height="35px"
        style={{ background: '#727177' }}
      />
    ) : (
      <DetailValue>{value}</DetailValue>
    );

  if (isLoading) return null;

  return (
    <DetailsStyle>
      <DetailFieldLeft>
        <DetailLabel>{t('your_cashback')}</DetailLabel>
        {renderSkeletonOrValue(cashbackValue)}
      </DetailFieldLeft>

      <DetailFieldRight>
        <DetailLabel>{t('collected')}</DetailLabel>
        {renderSkeletonOrValue(collectedValue)}

        <ShowOnDesktop>
          <Button
            variant={isDisabled ? 'gray' : 'orangeGradient'}
            onClick={getCashbackHandler}
            disabled={isDisabled}
          >
            <ButtonText>{t('get_cashback')}</ButtonText>
          </Button>
        </ShowOnDesktop>
      </DetailFieldRight>

      {showExpiryBox && (
        <ShowOnMobile>
          <ExpiryBox>
            <BodyTextRegularB color="#AFAFAF">
              {t('cashback_valid_until')}
            </BodyTextRegularB>
            <HeadingBoldH7>
              {formatTimestamp(data.tempCashback.expire)}
            </HeadingBoldH7>
          </ExpiryBox>
        </ShowOnMobile>
      )}
    </DetailsStyle>
  );
};
